/* eslint-disable new-cap */
import {FormControlMixin} from 'extensions/ui-mixins';
import {calculateDateWithOffsets, defineCustomElement, formatDate, formatIntlDate, formatIntlDateDayOfWeek} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import 'lit-flatpickr';
import {keyed} from 'lit/directives/keyed.js';
import {createRef, ref} from 'lit/directives/ref.js';
import './flatpickr.scss';
import stylesheet from './x-date-input.scss?as=stylesheet';

const TODAY_FORMATTED = formatDate(new Date());
const TODAY_TIMESTAMP = new Date(TODAY_FORMATTED).getTime();
const TODAY_LABEL = 'ASAP';

// Do not allow dates within 3 days, but the offset has to be 4 because today is not over yet.
const DAYS_OFFSET = 4;


export class DateInput extends FormControlMixin(LitElement) {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			disableDefaultAsap: {
				attribute: 'disable-default-asap',
				type: Boolean
			},
			displayDayOfWeek: {
				attribute: 'display-day-of-week',
				type: Boolean
			},
			name: {
				type: String
			},
			placeholder: {
				type: String
			}
		};
	}

	get isToday () {
		// eslint-disable-next-line no-extra-parens
		return (!this.value && !this.disableDefaultAsap) || this.value === TODAY_FORMATTED;
	}

	get label () {
		if (this.disableDefaultAsap && !this.value) {
			return this.placeholder;
		}

		if (this.isToday) {
			return TODAY_LABEL;
		}

		return this.displayDayOfWeek
			? formatIntlDateDayOfWeek(this.value)
			: formatIntlDate(this.value);
	}

	get disabledDates () {
		return [

			/**
			 * @param {Date} date
			 */
			(date) => {
				const day = date.getDay();
				const formattedDate = new Date(formatDate(date));
				const timestamp = formattedDate.getTime();

				// Disable weekends
				if (day === 0 || day === 6) {
					return true;
				}

				// Disable dates between now and DAYS_OFFSET constant
				const offsetDate = calculateDateWithOffsets(new Date(TODAY_FORMATTED), {
					days: DAYS_OFFSET
				});

				if (timestamp !== TODAY_TIMESTAMP && timestamp < offsetDate.getTime()) {
					return true;
				}

				return false;
			}
		];
	}

	/**
	 * @type {import('lit/directives/ref.js').Ref<HTMLInputElement>}
	 */
	inputRef = createRef();

	constructor () {
		super();

		/**
		 * @type {string}
		 */
		this.value = TODAY_FORMATTED;

		/**
		 * @type {boolean}
		 */
		this.disableDefaultAsap = false;

		/**
		 * @type {boolean}
		 */
		this.displayDayOfWeek = false;

		/**
		 * @type {string}
		 */
		this.placeholder = TODAY_LABEL;
	}

	handleLabelClick () {
		this.renderRoot?.querySelector('lit-flatpickr')?.open();
	}

	handleAsapButtonClick () {
		this.value = this.disableDefaultAsap
			? TODAY_FORMATTED
			: '';

		this.updateElementInternals();

		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			composed: true
		}));
	}

	/**
	 * Event handler for the input's change event.
	 */
	handleInputChange (e) {
		this.value = !this.disableDefaultAsap && e.target.value === TODAY_FORMATTED
			? ''
			: e.target.value;

		this.updateElementInternals();

		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			composed: true
		}));
	}

	render () {
		return html`
			<label class="label" for="input" @click="${this.handleLabelClick}">
				<span class="icon"></span>
				<span class="prompt">${keyed(this.value, this.label)}</span>
				<lit-flatpickr
					theme="none"
					dateFormat="Y-m-d"
					minDate="${TODAY_FORMATTED}"
					.disable="${this.disabledDates}"
					nextArrow=""
					prevArrow=""
				>
					<input ${ref(this.inputRef)} id="input" class="input" @change="${this.handleInputChange}" type="text" value="${this.value}" name="${this.name}" ?disabled="${this.disabled}" ?required="${this.required}" />
				</lit-flatpickr>
			</label>
			${this.isToday ? nothing : html`<button class="asap-button" @click="${this.handleAsapButtonClick}">${TODAY_LABEL}</button>`}
		`;
	}
};

defineCustomElement('x-date-input', DateInput);
